import type { AccentId, DialogueCategory } from '../types.js';

/**
 * Banter banks: category × accent. Accents are playful flavor, never mockery —
 * Spanish is sunny Spanglish, Russian is cheerful transliteration, English is
 * dry garden humor.
 */
export const LINES: Record<DialogueCategory, Record<AccentId, string[]>> = {
  match_start: {
    en: [
      "Let's make like leaves and fall on them!",
      'Nature always wins.',
      'Ready to get composted?',
      "I've been photosynthesizing for this.",
      'Hope you brought sunscreen!',
      'Time to bloom or bust.',
    ],
    es: [
      '¡Vamos, que ya es hora de la siesta!',
      'La naturaleza no perdona, cariño.',
      '¡A ver quién se hace el verde!',
      'Yo vengo del campo, amigo.',
      '¡Ay, cuidado con las espinas!',
      'Que gane el más verde.',
    ],
    ru: [
      "Pochemu my zdes', a ne v spine?",
      'My kak utro — medlennye, no yarkie.',
      "U vas est' shans. Ne bol'shoj, no est'.",
      'Vetru ok, ya — luchshe.',
      "Ne boytes' — my druz'ya... segodnya.",
      'Kak skazal list: poekhali!',
    ],
  },
  turn_start: {
    en: [
      'My turn to branch out.',
      'Watch this aim.',
      'Stretching my roots.',
      "Okay okay, I'm up.",
      'Let’s make this shot count.',
      'Who wants a haircut?',
    ],
    es: [
      'Ahora mando yo, ¡por favor!',
      'Mira mi puntería, ¡ay!',
      'Un estiramiento cortito.',
      'Ya llego, ya llego, ¡espera!',
      'Venga, sin tonterías.',
      '¿Alguien quiere poda?',
    ],
    ru: [
      'Snova ya. Ne skuchajte.',
      'Odin vystrel — i puf.',
      'Tsepochka... gotov.',
      'E, pozvonili? Idu.',
      "Prosto popadu. Prosto.",
      'Listik vperyod!',
    ],
  },
  hurt: {
    en: [
      'Ouch! Right in the petals!',
      "That's going to photosynthesize badly.",
      'Hey! I was growing there!',
      'I felt that one in my thorns.',
      'Rude! Extremely rude!',
      'Just a scratch. A big scratch.',
    ],
    es: [
      '¡Ay, caramba! ¡Mis hojas!',
      'Me has roto la savia, ¡ay!',
      '¡Oye! ¡Estaba germinando!',
      'Me dolió hasta la raíz.',
      '¡Qué maleducada eres!',
      'Solo ha sido un poquito... ¡del todo!',
    ],
    ru: [
      "Oj, mamochka! Bol'no!",
      'Ty menya... ty menya oboshel!',
      "E! Ya zdes' stoyal!",
      "Oj-oj-oj... vsyo, hvata.",
      "Nehorosho... ochen' nehorosho.",
      "Vsyo normal'no. Net, ne normal'no.",
    ],
  },
  taunt_hit: {
    en: [
      'Compost that!',
      'Straight from the canopy!',
      'Missed your watering, huh?',
      'That one had extra mulch.',
      'Blossom, right in the face!',
      'Consider that a weed whack.',
    ],
    es: [
      '¡A ver, a ver, chiquitín!',
      '¡Directito desde la rama!',
      'Se te olvidó regarte, ¿verdad?',
      'Con abono extra, ¡mira!',
      '¡Flor en la cara!',
      'Eso se llama poda, cariño.',
    ],
    ru: [
      'Vot tak, druzhok!',
      'Krasivo? Krasivo.',
      'Ty byl ne gotov.',
      'Ne ya vinovat, ty medlennyj.',
      'Tsvetok po nosu!',
      "Srezal tebya, kak travu.",
    ],
  },
  kill: {
    en: [
      'Potted!',
      'See you in the mulch.',
      "That's one less weed.",
      "You've been pruned.",
      'Bloomed and gone.',
      'Nailed it! Encore!',
    ],
    es: [
      '¡A la maceta y adiós!',
      'Hasta luego, gusanito.',
      'Un yuyo menos, ¡jeje!',
      '¡Podadita hecha!',
      'Se marchitó, ¡ay!',
      '¡En el clavo! ¡Otra!',
    ],
    ru: [
      'V gorshok tebya!',
      'Do skorogo, listik.',
      'Odin menee. Legche.',
      'Obrezal. Akkuratno.',
      'Tsvetok... i net tsvetka.',
      'Chotko! Yeshche raz!',
    ],
  },
  celebrate: {
    en: ["The garden's mine!", 'Flourish and exit.', 'Fields are won!'],
    es: ['¡El jardín es mío!', '¡Flor y salida!', '¡Campo conquistado!'],
    ru: ['Sad moy!', 'Vse — nashi!', 'Krasivo! My pobedili!'],
  },
  miss: {
    en: [
      "Wind's not my friend today.",
      'I meant to do that. Locally.',
      'Close! Sort of. Almost.',
      'The butterfly distracted me.',
      "Redefining 'near miss'.",
      'Okay, resetting my aim.',
    ],
    es: [
      'El viento me traiciona, ¡ay!',
      'Era una prueba, ¡solo una!',
      '¡Casi! Bueno... no.',
      'La mariposa... ¡ella empezó!',
      'Le he pasado de largo, ¡ay!',
      'Un segundito, un segundito.',
    ],
    ru: [
      'Vetru ne nravitsya.',
      'Ya prosto... razogryvalsya.',
      "Pochti. Ochen' pochti. Soglasen.",
      'Babochka vinovata.',
      'Mimo. Sovsem mimo.',
      "Eshche razok. Ser'ezno.",
    ],
  },
  low_hp: {
    en: [
      "I'm one sneeze from compost.",
      'Someone wrap me in aloe.',
      'Still photosynthesizing! Barely.',
      'My roots are showing.',
      'Careful, I bruise easily.',
      'A stiff breeze finishes me.',
    ],
    es: [
      'Estoy para el compost, ¡ay!',
      '¡Envuélveme en hojas!',
      'Sigo aquí... ¡de milagro!',
      'Se me ven las raíces, ¡ay!',
      '¡Con cuidado, que soy sensible!',
      'Un soplo y me voy.',
    ],
    ru: [
      "Mne ploho. Ochen' ploho.",
      "Nuzhen list, bol'shoj list.",
      'Yah... yah eshche zdes.',
      'Korni vidny. Ne krasivo.',
      "Ne trogajte menya. Ser'ezno.",
      'Odnogo udara... i vse.',
    ],
  },
  idle: {
    en: [
      'Are you drawing a map in there?',
      'My legs are falling asleep.',
      'Any day now.',
      'I could photosynthesize faster.',
      'The birds are judging you.',
      'Is the wind waiting too?',
    ],
    es: [
      '¿Estás dibujando un mapa?',
      'Se me duermen las nogi.',
      'Venga, venga, ya.',
      'Más rápido, por favor.',
      'Los pájaros te juzgan.',
      '¿El viento también espera?',
    ],
    ru: [
      "Risuesh' plan, chto li?",
      'Nogi ne chuvstvuyutsya.',
      'Nu-nu.',
      'Ya bystree by ustral.',
      'Ptitsy na tebya smotryat.',
      'I veter zhdet.',
    ],
  },
};
